import React, { useEffect } from 'react';
import './QuotationDisplay.css';

const QuotationDisplay = ({ 
  quotation, 
  formatDate, 
  formatCurrency, 
  getQuotationStatusColor,
  calculateQuotationTotal,
  onClose 
}) => {
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add event listener when component mounts
    document.addEventListener('keydown', handleEscapeKey);

    // Cleanup event listener when component unmounts
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  if (!quotation) {
    return (
      <div className="quotation-display">
        <div className="quotation-content">
          <p>Nenhuma cotação selecionada</p>
        </div>
      </div>
    );
  }

  const client = quotation.cliente || {};
  const bank = quotation.bank || {};
  const items = quotation.itens || [];
  
  const subtotal = items.reduce((total, item) => {
    const price = parseFloat(item.produto?.price || 0);
    const quantity = parseFloat(item.quantity || 0);
    return total + (price * quantity);
  }, 0);
  
  const discount = parseFloat(quotation.desconto || 0);
  const freight = parseFloat(quotation.valorTransporte || 0);
  const total = subtotal - discount + freight;

  const getStatusDisplay = (status) => {
    const statusMap = {
      'open': 'Aberta',
      'expired': 'Expirada',
      'accepted': 'Aceita',
      'rejected': 'Rejeitada'
    };
    return statusMap[status] || status;
  };

  const formatAddress = () => {
    const parts = [
      client.address,
      client.suburb,
      client.city,
      client.state,
      client.zipCode
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <div className="quotation-display">
      <div className="quotation-header">
        <div className="quotation-title">
          <h1>COTAÇÃO DE PREÇOS</h1>
          <p className="quotation-number">#{quotation.quotationId}</p>
        </div>
        <div className="quotation-status">
          <span 
            className="status-badge"
            style={{ backgroundColor: getQuotationStatusColor(quotation.quoteStatus) }}
          >
            {getStatusDisplay(quotation.quoteStatus)}
          </span>
        </div>
        <button className="close-btn" onClick={onClose} title="Fechar (ESC)">
          ✕
        </button>
      </div>

      <div className="quotation-content">
        {/* Company Header */}
        <div className="company-header">
          <div className="company-info">
            <h2>AEROTUBOS</h2>
            <p>Especializada em Tubos de Aço para Aviação</p>
            <div className="company-details">
              <p><strong>CNPJ:</strong> 01.976.367/0001-08</p>
              <p><strong>Endereço:</strong> Rua dos Hangares, 385 - Aeroporto</p>
              <p><strong>CEP:</strong> 18608-151 - Botucatu/SP</p>
              <p><strong>Telefone:</strong> (14) 3813-6040</p>
              <p><strong>Email:</strong> roberto@aerotubos.com</p>
            </div>
          </div>
        </div>

        {/* Quotation Info */}
        <div className="quotation-info">
          <div className="info-section">
            <h3>Informações da Cotação</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Data de Criação:</label>
                <span>{formatDate(quotation.timeCreated)}</span>
              </div>
              <div className="info-item">
                <label>Token de Acesso:</label>
                <span className="token">{quotation.token}</span>
              </div>
              <div className="info-item">
                <label>Validade:</label>
                <span>{quotation.validade || 'Não especificada'}</span>
              </div>
              <div className="info-item">
                <label>Emissão:</label>
                <span>{quotation.emissao || 'Pendente'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Client Information */}
        <div className="client-section">
          <h3>Dados do Cliente</h3>
          <div className="client-info">
            <div className="client-details">
              <div className="info-item">
                <label>Nome/Razão Social:</label>
                <span>{client.name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>ID Cliente:</label>
                <span>{quotation.clientId}</span>
              </div>
              <div className="info-item">
                <label>CPF/CNPJ:</label>
                <span>{client.id || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Email:</label>
                <span>{client.email || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Telefone:</label>
                <span>{client.landlineNumber || client.mobileNumber || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>Endereço:</label>
                <span>{formatAddress()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Banking Information */}
        {bank.bankName && (
          <div className="banking-section">
            <h3>Dados Bancários</h3>
            <div className="bank-info">
              <div className="info-item">
                <label>Banco:</label>
                <span>{bank.bankName}</span>
              </div>
              <div className="info-item">
                <label>Agência:</label>
                <span>{bank.branch}</span>
              </div>
              <div className="info-item">
                <label>Conta:</label>
                <span>{bank.accountNumber}</span>
              </div>
              <div className="info-item">
                <label>Titular:</label>
                <span>{bank.accountHolder}</span>
              </div>
            </div>
          </div>
        )}

        {/* Items Table */}
        <div className="items-section">
          <h3>Itens Cotados</h3>
          <div className="items-table-wrapper">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Material</th>
                  <th>Unidade</th>
                  <th>Quantidade</th>
                  <th>Preço Unit.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const produto = item.produto || {};
                  const quantity = parseFloat(item.quantity || 0);
                  const price = parseFloat(produto.price || 0);
                  const total = quantity * price;
                  
                  return (
                    <tr key={index}>
                      <td className="product-code">{produto.productId}</td>
                      <td className="product-description">
                        <div>
                          <strong>{produto.name}</strong>
                          {produto.description && (
                            <div className="product-specs">{produto.description}</div>
                          )}
                        </div>
                      </td>
                      <td>{produto.material || produto.Material}</td>
                      <td>{produto.unity}</td>
                      <td className="quantity">{quantity.toLocaleString('pt-BR')}</td>
                      <td className="price">{formatCurrency(price, produto.currency)}</td>
                      <td className="total">{formatCurrency(total, produto.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals */}
        <div className="totals-section">
          <div className="totals-table">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">{formatCurrency(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="total-row discount">
                <span className="total-label">Desconto:</span>
                <span className="total-value">-{formatCurrency(discount)}</span>
              </div>
            )}
            {freight > 0 && (
              <div className="total-row">
                <span className="total-label">Frete:</span>
                <span className="total-value">{formatCurrency(freight)}</span>
              </div>
            )}
            <div className="total-row final-total">
              <span className="total-label"><strong>Total:</strong></span>
              <span className="total-value"><strong>{formatCurrency(total)}</strong></span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {quotation.note && quotation.note.trim() && (
          <div className="notes-section">
            <h3>Observações</h3>
            <div className="notes-content">
              <p>{quotation.note}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="quotation-footer">
          <div className="footer-info">
            <p>Esta cotação é válida por 30 dias a partir da data de emissão.</p>
            <p>Para mais informações, entre em contato conosco.</p>
          </div>
          <div className="footer-signature">
            <p>Aerotubos - Tubos de Aço para Aviação</p>
          </div>
        </div>

        {/* Escape hint */}
        <div className="escape-hint">
          <span>Pressione ESC para voltar à lista</span>
        </div>
      </div>
    </div>
  );
};

export default QuotationDisplay;
